/* =====================================================================
   Balleh — hero slider (<hero-slider>)
   Scroll-snap carousel: native swipe + arrows + dots + optional autoplay.
   RTL-aware (scrollLeft sign), reduced-motion aware, a11y (aria-hidden on
   inactive slides, off-slide controls removed from the tab order, live region).
   ===================================================================== */
(function () {
  'use strict';

  class HeroSlider extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this.track = this.querySelector('[data-hs-track]');
      this.slides = Array.from(this.querySelectorAll('[data-hs-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-hs-dot]'));
      this.live = this.querySelector('[data-hs-live]');
      this.toggleBtn = this.querySelector('[data-hs-toggle]');
      this.count = this.slides.length;
      this.index = 0;
      if (!this.track || this.count <= 1) return;

      this.rtl = getComputedStyle(this).direction === 'rtl';
      this.autoplay = this.hasAttribute('data-autoplay');
      this.delay = Math.max(4000, parseInt(this.getAttribute('data-interval') || '6000', 10));
      this._userPaused = false;

      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-hs-next]')) this.goTo(this.index + 1, true);
        else if (e.target.closest('[data-hs-prev]')) this.goTo(this.index - 1, true);
        else if (e.target.closest('[data-hs-toggle]')) this.toggle();
        else { const d = e.target.closest('[data-hs-dot]'); if (d) this.goTo(this.dots.indexOf(d), true); }
      });

      this.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); this.goTo(this.index + (this.rtl ? -1 : 1), true); }
        else if (e.key === 'ArrowLeft') { e.preventDefault(); this.goTo(this.index + (this.rtl ? 1 : -1), true); }
      });

      // Active-slide detection (works for swipe AND programmatic scroll, RTL-agnostic).
      this.io = new IntersectionObserver((entries) => {
        let best = null, ratio = 0;
        for (const en of entries) if (en.intersectionRatio > ratio) { ratio = en.intersectionRatio; best = en.target; }
        if (best && ratio >= 0.5) { const i = this.slides.indexOf(best); if (i >= 0) this.setActive(i); }
      }, { root: this.track, threshold: [0.5, 0.75, 1] });
      this.slides.forEach((s) => this.io.observe(s));

      // Pause autoplay while the user is hovering, focused inside, or the tab is hidden.
      this.addEventListener('pointerenter', () => this._halt());
      this.addEventListener('pointerleave', () => this._run());
      this.addEventListener('focusin', () => this._halt());
      this.addEventListener('focusout', () => { if (!this.contains(document.activeElement)) this._run(); });
      document.addEventListener('visibilitychange', () => (document.hidden ? this._halt() : this._run()));

      this.setActive(0);
      this._run();
    }

    goTo(i, user) {
      i = (i + this.count) % this.count;
      const w = this.track.clientWidth;
      const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollTo({ left: i * w * (this.rtl ? -1 : 1), behavior: reduce ? 'auto' : 'smooth' });
      if (user && this.autoplay && !this._userPaused) { this._halt(); this._run(); } // reset timer after manual nav
    }

    setActive(i) {
      this.index = i;
      this.dots.forEach((d, n) => d.setAttribute('aria-current', n === i ? 'true' : 'false'));
      this.slides.forEach((s, n) => {
        const off = n !== i;
        s.setAttribute('aria-hidden', off ? 'true' : 'false');
        s.querySelectorAll('a, button, [tabindex]').forEach((el) => { el.tabIndex = off ? -1 : 0; });
      });
      if (this.live) this.live.textContent = (i + 1) + ' / ' + this.count;
    }

    _run() {
      if (!this.autoplay || this._userPaused || document.hidden || this._timer) return;
      this._timer = setInterval(() => this.goTo(this.index + 1, false), this.delay);
    }
    _halt() { if (this._timer) { clearInterval(this._timer); this._timer = null; } }

    toggle() {
      this._userPaused = !this._userPaused;
      if (this._userPaused) this._halt(); else this._run();
      if (this.toggleBtn) {
        this.toggleBtn.setAttribute('aria-pressed', this._userPaused ? 'true' : 'false');
        const lbl = this._userPaused ? this.toggleBtn.dataset.labelPlay : this.toggleBtn.dataset.labelPause;
        if (lbl) this.toggleBtn.setAttribute('aria-label', lbl);
      }
    }
  }

  if (!customElements.get('hero-slider')) customElements.define('hero-slider', HeroSlider);
})();
